/**
 * Per-assignment relationship metadata (relationships.metadata for form links).
 */

import { getScoringBreakdown } from './submissionAnswers';

export const FORM_ASSIGNMENT_METADATA_KEYS = Object.freeze({
  ALLOW_VIEW_ANSWERS_AFTER_SUBMISSION: 'allow_view_answers_after_submission',
  ALLOW_SEE_WRONG_ANSWERS_AFTER_SUBMISSION: 'allow_see_wrong_answers_after_submission',
  ALLOW_VIEW_POINTS_EARNED_AFTER_SUBMISSION: 'allow_view_points_earned_after_submission',
  // Assignee-facing collaboration policy. Cascades submission override → assignment → page permission.
  // Mirrors the staff chat.public.{read,write} / flags terminology.
  ASSIGNEE_PUBLIC_READ: 'assignee_public_read',
  ASSIGNEE_PUBLIC_WRITE: 'assignee_public_write',
  ASSIGNEE_FLAGS_READ: 'assignee_flags_read',
  ASSIGNEE_MARK_ADDRESSED: 'assignee_flags_mark_addressed',
  MAX_ATTEMPTS: 'max_attempts',
  RESUBMISSION_ALLOWED_AFTER_GRADING: 'resubmission_allowed_after_grading'
});

export function parseRelationshipMetadata(raw) {
  if (!raw) return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

export function defaultFormAssignmentMetadata() {
  return {
    [FORM_ASSIGNMENT_METADATA_KEYS.ALLOW_VIEW_ANSWERS_AFTER_SUBMISSION]: false,
    [FORM_ASSIGNMENT_METADATA_KEYS.ALLOW_SEE_WRONG_ANSWERS_AFTER_SUBMISSION]: false,
    [FORM_ASSIGNMENT_METADATA_KEYS.ALLOW_VIEW_POINTS_EARNED_AFTER_SUBMISSION]: true,
    [FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_PUBLIC_READ]: true,
    [FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_PUBLIC_WRITE]: true,
    [FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_FLAGS_READ]: true,
    [FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_MARK_ADDRESSED]: true,
    [FORM_ASSIGNMENT_METADATA_KEYS.MAX_ATTEMPTS]: 1,
    [FORM_ASSIGNMENT_METADATA_KEYS.RESUBMISSION_ALLOWED_AFTER_GRADING]: false
  };
}

function normalizeMaxAttempts(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.floor(n);
}

export function normalizeFormAssignmentMetadata(raw) {
  const meta = parseRelationshipMetadata(raw);
  return {
    ...defaultFormAssignmentMetadata(),
    [FORM_ASSIGNMENT_METADATA_KEYS.ALLOW_VIEW_ANSWERS_AFTER_SUBMISSION]:
      meta[FORM_ASSIGNMENT_METADATA_KEYS.ALLOW_VIEW_ANSWERS_AFTER_SUBMISSION] === true,
    [FORM_ASSIGNMENT_METADATA_KEYS.ALLOW_SEE_WRONG_ANSWERS_AFTER_SUBMISSION]:
      meta[FORM_ASSIGNMENT_METADATA_KEYS.ALLOW_SEE_WRONG_ANSWERS_AFTER_SUBMISSION] === true,
    [FORM_ASSIGNMENT_METADATA_KEYS.ALLOW_VIEW_POINTS_EARNED_AFTER_SUBMISSION]:
      meta[FORM_ASSIGNMENT_METADATA_KEYS.ALLOW_VIEW_POINTS_EARNED_AFTER_SUBMISSION] === true,
    [FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_PUBLIC_READ]:
      meta[FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_PUBLIC_READ] !== false,
    [FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_PUBLIC_WRITE]:
      meta[FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_PUBLIC_WRITE] === true,
    [FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_FLAGS_READ]:
      meta[FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_FLAGS_READ] !== false,
    [FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_MARK_ADDRESSED]:
      meta[FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_MARK_ADDRESSED] === true,
    [FORM_ASSIGNMENT_METADATA_KEYS.MAX_ATTEMPTS]: normalizeMaxAttempts(
      meta[FORM_ASSIGNMENT_METADATA_KEYS.MAX_ATTEMPTS]
    ),
    [FORM_ASSIGNMENT_METADATA_KEYS.RESUBMISSION_ALLOWED_AFTER_GRADING]:
      meta[FORM_ASSIGNMENT_METADATA_KEYS.RESUBMISSION_ALLOWED_AFTER_GRADING] === true
  };
}

export function buildFormAssignmentMetadataForSave(flags = {}) {
  return normalizeFormAssignmentMetadata(flags);
}

export function getFormAssignmentDefaults(formMetadata) {
  let meta = formMetadata;
  if (typeof meta === 'string') {
    try {
      meta = JSON.parse(meta);
    } catch {
      meta = {};
    }
  }
  meta = meta && typeof meta === 'object' && !Array.isArray(meta) ? meta : {};
  return normalizeFormAssignmentMetadata(meta.assignmentDefaults);
}

/** Relationship metadata overrides form setup defaults when present. */
export function resolveAssignmentMetadata(relationshipMetadata, formMetadata) {
  const defaults = getFormAssignmentDefaults(formMetadata);
  const rel = parseRelationshipMetadata(relationshipMetadata);
  if (!rel || Object.keys(rel).length === 0) return defaults;
  return normalizeFormAssignmentMetadata({ ...defaults, ...rel });
}

/** Effective policy for a submission attempt (form → relationship → override_settings). */
export function resolveSubmissionPolicy({
  formMetadata,
  relationshipMetadata,
  overrideSettings = null
}) {
  const base = resolveAssignmentMetadata(relationshipMetadata, formMetadata);
  const overrides = parseRelationshipMetadata(overrideSettings);
  if (!overrides || Object.keys(overrides).length === 0) return base;
  return normalizeFormAssignmentMetadata({ ...base, ...overrides });
}

export function allowsResubmission(policy) {
  const max = policy?.[FORM_ASSIGNMENT_METADATA_KEYS.MAX_ATTEMPTS] ?? 1;
  return Number(max) > 1;
}

/** Sidebar label + hint for how many attempts this assignment allows. */
export function getAttemptLimitDisplay(policy, submissions = []) {
  const maxAttempts = Math.max(1, Number(policy?.[FORM_ASSIGNMENT_METADATA_KEYS.MAX_ATTEMPTS]) || 1);
  const allowAfterGrading =
    policy?.[FORM_ASSIGNMENT_METADATA_KEYS.RESUBMISSION_ALLOWED_AFTER_GRADING] === true;
  const used = (submissions || []).length;

  if (maxAttempts === 1) {
    return {
      short: '1 attempt allowed',
      detail: allowAfterGrading
        ? 'One submission per assignment. You may start again after grading is complete.'
        : 'One submission only — no additional attempts after you turn in.'
    };
  }

  return {
    short: `${used} of ${maxAttempts} used`,
    detail: allowAfterGrading
      ? `Up to ${maxAttempts} attempts. You may submit again after grading is complete when attempts remain.`
      : `Up to ${maxAttempts} attempts. Each turn-in counts toward your limit.`
  };
}

/** Help text under the Submit action card on a draft attempt. */
export function getSubmitActionHelp(policy) {
  const maxAttempts = Math.max(1, Number(policy?.[FORM_ASSIGNMENT_METADATA_KEYS.MAX_ATTEMPTS]) || 1);
  const allowAfterGrading =
    policy?.[FORM_ASSIGNMENT_METADATA_KEYS.RESUBMISSION_ALLOWED_AFTER_GRADING] === true;

  if (maxAttempts === 1 && !allowAfterGrading) {
    return 'After you submit, you cannot edit your responses. This assignment allows only one attempt.';
  }
  if (maxAttempts === 1 && allowAfterGrading) {
    return 'After you submit, you cannot edit this attempt. You may start a new attempt once grading is complete.';
  }
  if (allowAfterGrading) {
    return 'After you submit, you cannot edit this attempt. You may start another attempt if you have attempts remaining, including after grading is complete.';
  }
  return 'After you submit, you cannot edit this attempt. You may start another attempt only if attempts remain.';
}

export function canStartNewSubmissionAttempt(policy, submissions = []) {
  const maxAttempts = Math.max(1, Number(policy?.[FORM_ASSIGNMENT_METADATA_KEYS.MAX_ATTEMPTS]) || 1);
  const allowAfterGrading =
    policy?.[FORM_ASSIGNMENT_METADATA_KEYS.RESUBMISSION_ALLOWED_AFTER_GRADING] === true;

  if ((submissions || []).length >= maxAttempts) {
    return { ok: false, reason: 'max_attempts_reached' };
  }

  const hasOpenDraft = (submissions || []).some((s) => s?.status === 'draft');
  if (hasOpenDraft) {
    return { ok: false, reason: 'draft_in_progress' };
  }

  const latestSubmitted = (submissions || []).find((s) => s?.status === 'submitted');
  if (
    latestSubmitted?.grading_status === 'complete' &&
    !allowAfterGrading
  ) {
    return { ok: false, reason: 'grading_complete' };
  }

  return { ok: true };
}

/**
 * Who may see the answer key / wrong-answer feedback / per-question scores in the assignment viewer.
 */
export function resolveAssignmentAnswerVisibility({
  assignmentMetadata,
  formMetadata,
  hasSubmission,
  gradingStatus = null,
  canViewQuestionAnswers = false,
  canUpdatePoints = false
}) {
  const meta = resolveAssignmentMetadata(assignmentMetadata, formMetadata);
  const staff = canViewQuestionAnswers || canUpdatePoints;
  const gradingComplete = gradingStatus === 'complete';

  const showWrongFeedback =
    staff ||
    (hasSubmission &&
      meta[FORM_ASSIGNMENT_METADATA_KEYS.ALLOW_SEE_WRONG_ANSWERS_AFTER_SUBMISSION]);

  const showPointsEarned =
    hasSubmission &&
    gradingComplete &&
    meta[FORM_ASSIGNMENT_METADATA_KEYS.ALLOW_VIEW_POINTS_EARNED_AFTER_SUBMISSION] === true;

  return {
    showAnswerKey:
      staff ||
      (hasSubmission && meta[FORM_ASSIGNMENT_METADATA_KEYS.ALLOW_VIEW_ANSWERS_AFTER_SUBMISSION]),
    showWrongFeedback,
    /** Per-question earned/max — separate from correct/incorrect feedback. */
    showQuestionScores: staff || showWrongFeedback || showPointsEarned
  };
}

export function buildScoringFeedbackMap(formData, { revealCorrectness = true } = {}) {
  const breakdown = getScoringBreakdown(formData);
  if (!Array.isArray(breakdown) || breakdown.length === 0) return {};
  const map = {};
  breakdown.forEach((row) => {
    if (!row?.questionId) return;
    const pending =
      !row.graded_by &&
      (row.pendingReview === true ||
        (row.manual === true && row.earned == null && row.pendingReview !== false));

    let correct = null;
    let neutral = false;

    if (revealCorrectness && !pending) {
      if (row.manual === true) {
        const earned = Number(row.earned);
        const max = Number(row.max);
        if (Number.isFinite(earned) && Number.isFinite(max) && earned >= max) {
          correct = true;
        } else {
          neutral = true;
        }
      } else {
        correct = row.correct === true;
      }
    }

    map[row.questionId] = {
      correct,
      neutral,
      pending: revealCorrectness ? pending : false,
      pointsOnly: !revealCorrectness,
      earned: row.earned,
      max: row.max
    };
  });
  return map;
}
